import React, { useState } from "react";
import { useParams } from "react-router-dom";
import useClientDetails from "../../../../../hooks/useClientDetails";
import { useQuery, useQueryClient } from "react-query";
import { QK } from "../../../../../base/qk";
import {
  deleteEnvironmentalRiskAssessment,
  getClientEnvironmentalRiskAssessment,
  getEnvironmentalQuestions,
} from "../../../../../api/services/assessment";
import { formatDateField } from "../../../../../base/FormatTime";
import { AddCircle, Delete, Info, Visibility } from "@mui/icons-material";
import TableComponent from "../../../../TableComponent/TableComponent";
import { Box, Button, Card, Chip, Stack, Typography } from "@mui/material";
import CreateEnvironmentalRiskAssessmentModal from "./CreateEnvironmentalRiskAssessmentModal";
import EnvironmentalAssessmentDetailsModal from "./EnvironmentalAssessmentDetailsModal";
import { useSnackbar } from "notistack";
import ConfirmModal from "../../../../ConfirmModal/ConfirmModal";
import {
  environmentalRiskLevels,
  getEnvRiskLevelColor,
} from "./environmentalAssessment.config";
import AssessmentRiskTooltip from "../ClientAssessments/AssessmentRiskTooltip";

const EnvironmentalRiskAssessment = () => {
  const { id: clientId } = useParams();
  const { clientDetails, fullName } = useClientDetails(clientId);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [showCreateAssessmentModal, setShowCreateAssessmentModal] =
    useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: environmentalAssessments, isLoading } = useQuery(
    [QK.environmentalAssessments, clientId],
    () => getClientEnvironmentalRiskAssessment(clientId),
    { enabled: !!clientId, keepPreviousData: true }
  );
  const { data: environmentalQuestions, isLoading: questionsLoading } =
    useQuery([QK.environmentalQuestions], () => getEnvironmentalQuestions(), {
      enabled: true,
      keepPreviousData: true,
    });

  const handleCreateNewRiskAssessment = () => {
    setShowCreateAssessmentModal(true);
  };
  const handleShowDetailsModal = (assessment) => {
    setSelectedAssessment(assessment);
    setShowDetailsModal(true);
  };

  const handleDelete = (id) => {
    setAssessmentId(id);
    setShowConfirmModal(true);
  };
  const handleConfirmDelete = async () => {
    const response = await deleteEnvironmentalRiskAssessment(assessmentId);
    if (response.status === 204) {
      setShowConfirmModal(false);
      enqueueSnackbar("Assessment deleted successfully", {
        variant: "success",
      });
      queryClient.invalidateQueries(QK.environmentalAssessments);
    } else {
      enqueueSnackbar("Failed to delete", { variant: "error" });
    }
  };

  const columns = [
    {
      field: "date_completed",
      headerName: "Date",
      render: (value) => formatDateField(value),
    },

    { field: "client_name", headerName: "Service User" },
    { field: "assessor_name", headerName: "Assessor Name" },
    { field: "score", headerName: "Score" },
    {
      field: "risk_level",
      headerName: "Risk Level",
      render: (value) => (
        <Chip label={value} color={getEnvRiskLevelColor(value)} size="small" />
      ),
    },
    {
      field: "next_review_date",
      headerName: "Next Review Date",
      render: (value) => formatDateField(value),
    },
    {
      field: "action_required",
      headerName: "Action Required",
    },
  ];

  const actions = [
    {
      icon: <Visibility />,
      color: "primary",
      handler: (assessment) => handleShowDetailsModal(assessment),
    },
    {
      icon: <Delete />,
      color: "error",
      handler: (assessment) => handleDelete(assessment.id),
    },
  ];
  return (
    <div className="w-75 mx-auto">
      <Card sx={{ mb: 3 }}>
        <Box sx={{ bgcolor: "white", color: "black", p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box>
              <div className="d-flex gap-2">
                <Typography variant="h5">
                  Environmental Risk Assessment for <strong>{fullName}</strong>
                </Typography>
                <AssessmentRiskTooltip
                  assessmentType="Environmental"
                  riskLevels={environmentalRiskLevels}
                  icon={Info}
                  iconColor="info"
                  size="small"
                  placement="right"
                />
              </div>
              <Typography variant="body1">
                <strong>NHS Number:</strong>{" "}
                {clientDetails?.nhs_number || "Not Set"}
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddCircle />}
              color="primary"
              onClick={handleCreateNewRiskAssessment}
            >
              New Assessment
            </Button>
          </Stack>
        </Box>
      </Card>
      <div>
        <TableComponent
          data={environmentalAssessments || []}
          columns={columns}
          actions={actions}
          isLoading={isLoading}
          size="small"
        />
      </div>
      <CreateEnvironmentalRiskAssessmentModal
        open={showCreateAssessmentModal}
        handleClose={() => setShowCreateAssessmentModal(false)}
        questions={environmentalQuestions || []}
        clientName={fullName}
      />
      <EnvironmentalAssessmentDetailsModal
        open={showDetailsModal}
        handleClose={() => setShowDetailsModal(false)}
        assessment={selectedAssessment}
        questions={environmentalQuestions}
      />
      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirmDelete}
        title="Delete Assessment"
        body="Are you sure want to delete this assessment"
      />
    </div>
  );
};

export default EnvironmentalRiskAssessment;
